/**
 * Лента сообщений диалога (C-07) с виртуализацией (CLIENT_WEB.md §11.2 п. 1).
 *
 * Почему отдельный компонент: у истории три правила прокрутки, которые
 * приходится соблюдать одновременно, и держать их вперемешку с разметкой
 * экрана невозможно —
 *  1) «липкий низ»: новое сообщение прокручивает ленту, только если
 *     пользователь и так внизу (CC §2.6 п. 5);
 *  2) якорь по `seq` последнего видимого сообщения — по нему восстанавливается
 *     позиция после перестройки раскладки (D-03);
 *  3) при вставке старой страницы сверху позиция компенсируется по изменению
 *     высоты, иначе лента «прыгает» под пальцем.
 *
 * Виртуализация включена всегда, без порога: §11.2 п. 1 требует её для
 * истории как таковой, а переключение режима на ходу теряло бы позицию
 * прокрутки ровно в тот момент, когда переписка становится длинной.
 */
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type Ref,
} from 'react'

import { senderColor, type SenderMap } from '@features/channels/use-channel-members'
import { PENDING_SEQ_BASE, type LocalMessage } from '@shared/db'
import type { ParsedLink } from '@shared/lib/links'
import { Avatar, Badge, Icon, Spinner, cx } from '@shared/ui'

import { formatDaySeparator, needsDaySeparator } from '../../lib/format'
import { MessageBubble } from './MessageBubble'
import styles from './message.module.css'

/** Сообщения одного автора внутри этого окна группируются без «хвостика». */
const GROUPING_WINDOW_MS = 4 * 60 * 1000

/** Порог «внизу»: пользователь считается внизу, даже если чуть отлистнул. */
const STICKY_THRESHOLD_PX = 80

/** С какого расстояния до конца показывать кнопку «вниз». */
const JUMP_THRESHOLD_PX = 320

/** Средняя высота пузыря до измерения. */
const ESTIMATED_BUBBLE_HEIGHT = 72

const OVERSCAN = 8

export interface MessageHistoryHandle {
  /** Прокрутка к сообщению по `seq` — переход к ответу и результату поиска. */
  scrollToSeq: (seq: number) => void
  /** `seq` последнего полностью видимого сообщения — якорь D-03. */
  lastVisibleSeq: () => number | null
  /** Восстановление позиции по якорю после перестройки раскладки. */
  restoreToSeq: (seq: number) => boolean
}

interface MessageHistoryProps {
  handleRef: Ref<MessageHistoryHandle>
  messages: readonly LocalMessage[]
  myUserId: string
  counterpartName: string | null
  highlightSeq: number | null
  loadingOlder: boolean
  hidden: boolean
  empty: React.ReactNode
  typing: React.ReactNode
  /** Подгрузка старой страницы; `false` — страниц больше нет. */
  onLoadOlder: () => Promise<boolean | void> | void
  onOpenMedia: (mediaId: string) => void
  onContextMenu: (message: LocalMessage, anchor: DOMRect) => void
  onRetry: (message: LocalMessage) => void
  onDiscard: (message: LocalMessage) => void
  onOpenLink: (link: ParsedLink) => void
  onReact: (message: LocalMessage, reaction: string | null) => void
  /** Свайп вправо по сообщению — ответ на него (как в Telegram). */
  onReply: (message: LocalMessage) => void
  /** Реакция по двойному касанию — первая из `config.reactions`. */
  quickReaction: string | null
  /** Пост канала: открыть обсуждение (изменение v1.8). Не задан — комментариев нет. */
  onOpenComments?: ((message: LocalMessage) => void) | undefined
  onAnchorChange: (seq: number | null) => void
  /** `seq`, с которого начинаются непрочитанные при открытии — перед ним плашка «Новые сообщения». */
  firstUnreadSeq: number | null
  /**
   * Участники группы по id (изменение v1.10): над чужим сообщением встаёт имя автора, слева —
   * его аватар. В переписке и в канале не задаётся: там собеседник один, а посты канала идут
   * от самого канала — как в Telegram.
   */
  senders?: SenderMap | undefined
}

/** Последний серверный `seq`: свои неотправленные сообщения несут временный, заведомо больший. */
function lastConfirmedSeq(messages: readonly LocalMessage[]): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const seq = messages[index]?.seq ?? 0
    if (seq < PENDING_SEQ_BASE) return seq
  }
  return 0
}

/** Два соседних сообщения образуют серию: один автор, короткая пауза между ними. */
function sameSeries(a: LocalMessage | undefined, b: LocalMessage | undefined): boolean {
  if (!a || !b) return false
  if (a.sender_id === null || b.sender_id === null) return false
  if (a.sender_id !== b.sender_id) return false
  return Math.abs(Date.parse(b.created_at) - Date.parse(a.created_at)) < GROUPING_WINDOW_MS
}

export function MessageHistory({
  handleRef,
  messages,
  myUserId,
  counterpartName,
  highlightSeq,
  loadingOlder,
  hidden,
  empty,
  typing,
  onLoadOlder,
  onOpenMedia,
  onContextMenu,
  onRetry,
  onDiscard,
  onOpenLink,
  onReact,
  onReply,
  quickReaction,
  onOpenComments,
  onAnchorChange,
  firstUnreadSeq,
  senders,
}: MessageHistoryProps): React.ReactElement {
  const scrollRef = useRef<HTMLDivElement>(null)
  /** Внутренний блок высотой в полную ленту — по нему видно, когда содержимое дорисовалось. */
  const contentRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef(true)
  const previousHeightRef = useRef(0)
  const loadingRef = useRef(false)
  /** Старых страниц больше нет — повторно не запрашиваем до смены диалога. */
  const exhaustedRef = useRef(false)
  /** Прошлое положение прокрутки — по нему видно направление движения. */
  const lastTopRef = useRef(0)
  /**
   * Позиция, выставленная кодом. Событие прокрутки с ней — не жест
   * пользователя: направление движения по нему не считается, «липкий низ»
   * не выключается.
   */
  const programmaticTopRef = useRef<number | null>(null)
  /** До этого момента события прокрутки считаются откликом на нашу установку позиции. */
  const programmaticUntilRef = useRef(0)
  /** Якорь по `seq` последнего видимого сообщения — обновляется на прокрутке. */
  const anchorSeqRef = useRef<number | null>(null)
  /** Лента отлистана вверх — показать кнопку «к последним сообщениям». */
  const [awayFromBottom, setAwayFromBottom] = useState(false)
  /** Последний seq на момент ухода от низа — по нему считаются «новые ниже» для кнопки «вниз». */
  const awaySinceSeqRef = useRef<number | null>(null)
  /** Сообщения, пришедшие после открытия, появляются с анимацией — по одному разу. */
  const initialMaxSeqRef = useRef<number | null>(null)
  const enteredRef = useRef(new Set<string>())
  /** Плавающая плашка даты верхнего видимого сообщения — пока идёт прокрутка (как в Telegram). */
  const [floatingDate, setFloatingDate] = useState<string | null>(null)
  /** Текст плашки остаётся на время её исчезновения — иначе она схлопывается в пустую точку. */
  const [floatingVisible, setFloatingVisible] = useState(false)
  const floatingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_BUBBLE_HEIGHT,
    overscan: OVERSCAN,
    getItemKey: (index) => messages[index]?.seq ?? index,
  })

  const indexOfSeq = useCallback(
    (seq: number) => messages.findIndex((message) => message.seq === seq),
    [messages],
  )

  const scrollToSeq = useCallback(
    (seq: number) => {
      const index = indexOfSeq(seq)
      if (index < 0) return
      // Переход к конкретному сообщению — намерение уйти от конца: «липкий низ»
      // снимается сразу, а якорь ставится на цель, иначе изменение высоты
      // (закрывшаяся клавиатура) вернуло бы ленту к прежнему якорю у конца.
      stickyRef.current = false
      anchorSeqRef.current = seq
      programmaticTopRef.current = null
      // Окно «успокоения»: события прокрутки перехода не считаются жестом и не
      // возвращают «липкий низ» по ещё не измеренной (короткой) высоте ленты.
      programmaticUntilRef.current = performance.now() + 400
      virtualizer.scrollToIndex(index, { align: 'center' })
    },
    [indexOfSeq, virtualizer],
  )

  /** `seq` последнего сообщения, целиком попадающего в видимую область. */
  const lastVisibleSeq = useCallback((): number | null => {
    const element = scrollRef.current
    if (!element) return null

    const visible = virtualizer.getVirtualItems()
    const bottom = element.scrollTop + element.clientHeight

    for (let index = visible.length - 1; index >= 0; index -= 1) {
      const row = visible[index]
      if (row && row.end <= bottom) return messages[row.index]?.seq ?? null
    }

    return null
  }, [messages, virtualizer])

  const restoreToSeq = useCallback(
    (seq: number): boolean => {
      const index = indexOfSeq(seq)
      if (index < 0) return false

      // Восстановление якоря (D-03, открытие диалога) «липкий низ» не трогает:
      // якорь у конца ленты обычно и есть последнее видимое сообщение, и новые
      // сообщения после него должны по-прежнему прокручивать ленту вниз.
      programmaticTopRef.current = null
      programmaticUntilRef.current = performance.now() + 400
      virtualizer.scrollToIndex(index, { align: 'end' })
      return true
    },
    [indexOfSeq, virtualizer],
  )

  useImperativeHandle(handleRef, () => ({ scrollToSeq, lastVisibleSeq, restoreToSeq }), [
    scrollToSeq,
    lastVisibleSeq,
    restoreToSeq,
  ])

  /* --- Прокрутка -------------------------------------------------------- */

  /**
   * К самому концу — по фактической высоте прокрутки, а не по оценке
   * виртуализатора: `scrollToIndex` считает конец по ещё не измеренным
   * строкам и останавливается выше него, а такое событие прокрутки
   * выглядело бы как уход пользователя вверх.
   */
  const pinToBottom = useCallback((): void => {
    const element = scrollRef.current
    if (!element) return

    const target = Math.max(0, element.scrollHeight - element.clientHeight)
    programmaticTopRef.current = target
    programmaticUntilRef.current = performance.now() + 200
    element.scrollTop = target
    lastTopRef.current = element.scrollTop
  }, [])

  const lastSeq = messages.at(-1)?.seq ?? 0

  useLayoutEffect(() => {
    const element = scrollRef.current
    if (!element || !stickyRef.current) return

    pinToBottom()

    /*
     * Второй проход в следующем кадре: при динамическом измерении высота
     * строк уточняется уже ПОСЛЕ отрисовки, и одной прокрутки к концу не
     * хватает — лента останавливается чуть выше него.
     */
    const settle = requestAnimationFrame(() => {
      if (!stickyRef.current) return
      pinToBottom()
      setAwayFromBottom(false)
    })

    return () => cancelAnimationFrame(settle)
  }, [lastSeq, messages.length, pinToBottom])

  const handleScroll = useCallback(async () => {
    const element = scrollRef.current
    if (!element) return

    /*
     * «Липкий низ» выключает только сам пользователь, уходя вверх. Считать
     * его по одному расстоянию до конца нельзя: при динамическом измерении
     * высота ленты уточняется после отрисовки, и лента «отклеивалась» бы
     * сама собой, переставая показывать новые сообщения.
     */
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight
    const settling = performance.now() < programmaticUntilRef.current
    const programmatic =
      settling ||
      (programmaticTopRef.current !== null && Math.abs(element.scrollTop - programmaticTopRef.current) <= 1)

    const scrolledUp = !programmatic && element.scrollTop < lastTopRef.current - 4
    lastTopRef.current = element.scrollTop

    // Пока идёт программный переход, «липкость» не пересчитывается: сразу после
    // показа скрытой ленты её высота ещё оценочная, и «внизу» было бы ложным.
    if (!settling) {
      if (distance < STICKY_THRESHOLD_PX) stickyRef.current = true
      else if (scrolledUp) stickyRef.current = false
    }

    const away = distance > JUMP_THRESHOLD_PX
    if (away && awaySinceSeqRef.current === null) awaySinceSeqRef.current = lastConfirmedSeq(messages)
    if (!away) awaySinceSeqRef.current = null
    setAwayFromBottom(away)

    // Плашка даты: по верхней видимой строке, гаснет через секунду после остановки.
    if (!programmatic) {
      const topRow = virtualizer.getVirtualItems().find((row) => row.end > element.scrollTop + 8)
      const topMessage = topRow ? messages[topRow.index] : undefined
      if (topMessage && element.scrollTop > 40) {
        setFloatingDate(formatDaySeparator(topMessage.created_at))
        setFloatingVisible(true)
        if (floatingTimerRef.current) clearTimeout(floatingTimerRef.current)
        floatingTimerRef.current = setTimeout(() => setFloatingVisible(false), 1000)
      }
    }

    const anchor = lastVisibleSeq()
    anchorSeqRef.current = anchor
    onAnchorChange(anchor)

    /*
     * Старая страница подгружается, только когда ПОЛЬЗОВАТЕЛЬ листает вверх:
     * программная прокрутка к последнему сообщению проходит через верх ленты,
     * и без этого условия открытие диалога запускало подгрузку, а её
     * компенсация сбивала прокрутку к концу.
     */
    if (!scrolledUp || element.scrollTop >= 120 || loadingRef.current || exhaustedRef.current) return

    loadingRef.current = true
    previousHeightRef.current = element.scrollHeight

    let loaded: boolean | void
    try {
      loaded = await onLoadOlder()
    } catch {
      loaded = false
    }

    if (loaded === false) {
      // Страниц больше нет: компенсировать нечего, а «поправка» по изменившейся
      // по другим причинам высоте (клавиатура, показ ленты) уводила бы вниз.
      exhaustedRef.current = true
      loadingRef.current = false
      return
    }

    /*
     * Компенсация вставки сверху: без неё лента прыгает ровно на высоту
     * добавленной страницы. Высоту берём после отрисовки, поэтому
     * возвращаем позицию в следующем кадре.
     */
    requestAnimationFrame(() => {
      const current = scrollRef.current
      if (current) current.scrollTop += current.scrollHeight - previousHeightRef.current
      loadingRef.current = false
    })
  }, [lastVisibleSeq, messages, onAnchorChange, onLoadOlder, virtualizer])

  useEffect(() => {
    return () => {
      if (floatingTimerRef.current) clearTimeout(floatingTimerRef.current)
    }
  }, [])

  /** Кнопка «вниз»: вернуться к последним сообщениям и снова «прилипнуть». */
  const jumpToBottom = useCallback(() => {
    const element = scrollRef.current
    if (!element) return

    stickyRef.current = true
    programmaticTopRef.current = Math.max(0, element.scrollHeight - element.clientHeight)
    // Плавная прокрутка — только если система не просит убавить движение.
    const reduce = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    element.scrollTo({ top: element.scrollHeight, behavior: reduce ? 'auto' : 'smooth' })
    awaySinceSeqRef.current = null
    setAwayFromBottom(false)
  }, [])

  // Граница «старое / новое» — по первой непустой ленте: всё, что придёт позже, появляется с анимацией.
  useEffect(() => {
    if (initialMaxSeqRef.current === null && messages.length) {
      initialMaxSeqRef.current = messages[messages.length - 1]?.seq ?? 0
    }
  }, [messages])

  const awaySince = awayFromBottom ? awaySinceSeqRef.current : null
  const newBelow =
    awaySince === null
      ? 0
      : messages.reduce(
          (count, item) => count + (item.seq > awaySince && item.sender_id !== myUserId ? 1 : 0),
          0,
        )

  // Смена диалога возвращает «липкий низ»: новая переписка открывается снизу.
  const conversationId = messages[0]?.conversation_id ?? null
  useEffect(() => {
    stickyRef.current = true
    anchorSeqRef.current = null
    exhaustedRef.current = false
    setAwayFromBottom(false)
  }, [conversationId])

  /*
   * «Липкий низ» держится и при изменении высоты СОДЕРЖИМОГО: строки
   * измеряются после отрисовки, приходят вложения, раскрываются картинки —
   * пока пользователь внизу, лента остаётся у последнего сообщения.
   */
  useEffect(() => {
    const content = contentRef.current
    if (!content || typeof ResizeObserver === 'undefined') return

    let lastHeight = content.offsetHeight
    const observer = new ResizeObserver(() => {
      // Скрытие и показ ленты (поиск C-08) — не рост содержимого: позицию не трогаем.
      const wasHidden = lastHeight === 0 || content.offsetHeight === 0
      lastHeight = content.offsetHeight
      if (wasHidden || !stickyRef.current || loadingRef.current) return
      pinToBottom()
    })

    observer.observe(content)
    return () => observer.disconnect()
  }, [pinToBottom])

  /*
   * Изменение высоты видимой области — экранная клавиатура (IOS §3.2 п. 4),
   * сужение окна (D-03), поворот. Правило CC §2.6 п. 5: позиция держится по
   * `seq` последнего видимого сообщения, а не по пикселям, — «липкий низ»
   * остаётся внизу, остальные видят то же сообщение, что и до изменения.
   */
  useEffect(() => {
    const element = scrollRef.current
    if (!element || typeof ResizeObserver === 'undefined') return

    let lastHeight = element.clientHeight
    const observer = new ResizeObserver(() => {
      const current = scrollRef.current
      if (!current || current.clientHeight === lastHeight) return

      // Скрытие и показ ленты (поиск C-08) — не изменение окна: позицию,
      // выставленную переходом к сообщению, трогать нельзя.
      const wasHidden = lastHeight === 0 || current.clientHeight === 0
      lastHeight = current.clientHeight
      if (wasHidden) return

      if (stickyRef.current) {
        pinToBottom()
        // Второй проход после того, как на изменение высоты отреагировал виртуализатор.
        requestAnimationFrame(() => {
          if (stickyRef.current) pinToBottom()
        })
        return
      }

      const anchor = anchorSeqRef.current
      if (anchor !== null) restoreToSeq(anchor)
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [pinToBottom, restoreToSeq])

  /* --- Отрисовка --------------------------------------------------------- */

  // Первое непрочитанное входящее — перед ним плашка «Новые сообщения» (как в Telegram).
  const unreadIndex =
    firstUnreadSeq === null
      ? -1
      : messages.findIndex((message) => message.seq >= firstUnreadSeq && message.sender_id !== myUserId)

  const renderRow = (message: LocalMessage, index: number): React.ReactNode => {
    const previous = messages[index - 1]
    const next = messages[index + 1]
    const daySeparator = needsDaySeparator(previous?.created_at ?? null, message.created_at)

    // Серия одного автора: продолжение — без верхнего скругления у стыка,
    // хвостик — только у последнего сообщения серии (день серию прерывает).
    const grouped = !daySeparator && sameSeries(previous, message)
    const nextDay = next ? needsDaySeparator(message.created_at, next.created_at) : true
    const tail = nextDay || !sameSeries(message, next)
    /*
     * Новое сообщение появляется с анимацией один раз: входящее — мягко снизу,
     * своё — «вылетает» из строки ввода. Ключ — client_message_id: у своего
     * сообщения id меняется с локального на серверный при подтверждении.
     */
    const enterKey = message.client_message_id || message.id
    const fresh =
      initialMaxSeqRef.current !== null &&
      message.seq > initialMaxSeqRef.current &&
      !enteredRef.current.has(enterKey)
    const entering = fresh ? (message.sender_id === myUserId ? 'out' : 'in') : false

    /*
     * Автор чужого сообщения в группе: имя своим цветом над первым пузырём серии и аватар
     * под последним. `senders` задан только у группы — в переписке и в канале подписи нет.
     */
    const senderId = message.sender_id
    const member = senders && senderId !== null ? senders.get(senderId) : undefined
    const sender =
      senders && senderId !== null && senderId !== myUserId
        ? { name: member?.name ?? null, color: senderColor(senderId) }
        : undefined
    const replyAuthorId = message.reply_to?.sender_id ?? null
    const replyAuthorName =
      senders && replyAuthorId !== null ? (senders.get(replyAuthorId)?.name ?? 'Участник') : null

    return (
      <>
        {daySeparator ? (
          <div className={styles['daySeparator']}>{formatDaySeparator(message.created_at)}</div>
        ) : null}

        {index === unreadIndex ? (
          <div className={cx(styles['daySeparator'], styles['unreadSeparator'])}>Новые сообщения</div>
        ) : null}

        <MessageBubble
          message={message}
          mine={message.sender_id === myUserId}
          grouped={grouped}
          tail={tail}
          highlighted={highlightSeq === message.seq}
          counterpartName={counterpartName}
          myUserId={myUserId}
          onOpenMedia={onOpenMedia}
          onContextMenu={onContextMenu}
          onRetry={onRetry}
          onDiscard={onDiscard}
          onScrollToReply={scrollToSeq}
          onOpenLink={onOpenLink}
          onReact={onReact}
          onReply={onReply}
          quickReaction={quickReaction}
          onOpenComments={onOpenComments}
          entering={entering}
          onEntered={() => enteredRef.current.add(enterKey)}
          sender={sender}
          senderAvatar={
            sender ? <Avatar name={member?.name ?? null} photo={member?.photo ?? null} size="xs" /> : null
          }
          replyAuthorName={replyAuthorName}
        />
      </>
    )
  }

  return (
    <div className={styles['historyWrap']} hidden={hidden}>
      <div className={styles['history']} ref={scrollRef} onScroll={() => void handleScroll()}>
        {loadingOlder ? (
          <div className={styles['historyLoader']}>
            <Spinner size={20} />
          </div>
        ) : null}

        {messages.length === 0 ? empty : null}

        <div
          ref={contentRef}
          className={styles['historyInner']}
          style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
        >
          {virtualizer.getVirtualItems().map((row) => {
            const message = messages[row.index]
            if (!message) return null

            return (
              <div
                key={row.key}
                ref={virtualizer.measureElement}
                data-index={row.index}
                data-seq={message.seq}
                style={{
                  position: 'absolute',
                  insetBlockStart: 0,
                  insetInline: 0,
                  transform: `translateY(${row.start}px)`,
                }}
              >
                {renderRow(message, row.index)}
              </div>
            )
          })}
        </div>

        {typing}
      </div>

      <div
        className={cx(styles['floatingDate'], floatingVisible && styles['floatingDateVisible'])}
        aria-hidden
      >
        {floatingDate}
      </div>

      <button
        type="button"
        className={cx(styles['jumpButton'], awayFromBottom && styles['jumpButtonVisible'])}
        aria-label="К последним сообщениям"
        aria-hidden={!awayFromBottom}
        tabIndex={awayFromBottom ? 0 : -1}
        onClick={jumpToBottom}
      >
        <Icon name="arrow-down" size={22} />
        {newBelow ? (
          <span className={styles['jumpBadge']}>
            <Badge count={newBelow} />
          </span>
        ) : null}
      </button>
    </div>
  )
}
